import type { Track } from "../../shared/domain.ts";
import { tracksMatching, type Filter } from "./pools.ts";

/**
 * Mixes — the named Filters Unlimited is chosen from.
 *
 * Editorial on purpose. A genre × era matrix is 36 cells, ten of them under the
 * Pool floor, and none of them says anything; a dozen named crossings say what
 * you are about to hear. The raw axes are still reachable through Custom, so
 * nothing is taken away — it is just no longer the first thing you meet.
 *
 * Every Mix here is asserted against the live Catalog in `mixes.test.ts`. Adding
 * one whose Pool is under the floor fails the suite rather than shipping a mode
 * that repeats itself.
 */
export interface Mix {
  id: string;
  name: string;
  /** One line, shown under the name on the card. */
  blurb: string;
  filter: Filter;
  /**
   * How the card looks. Editorial, so it lives with the Mix rather than being
   * derived from the Filter — "Deep Cuts" and "Chart Toppers" have identical
   * genre and era axes and should not look alike.
   */
  glyph: string;
  /** An HSL hue, matching the genre and era hues in components/labels.ts. */
  hue: number;
}

/** The id standing in for a player-built Filter rather than one of the Mixes. */
export const CUSTOM_MIX_ID = "custom";

/** Pool sizes in the comments are from the 3000-Track Catalog built 2026-08-09. */
export const MIXES: readonly Mix[] = [
  {
    id: "everything",
    glyph: "🎵",
    hue: 280,
    name: "Everything",
    blurb: "The whole catalog, no filter",
    filter: { genres: [], decades: [], band: null },
  }, // 3000
  {
    id: "throwback",
    glyph: "📼",
    hue: 292,
    name: "Throwback",
    blurb: "The 90s and everything before",
    filter: { genres: [], decades: ["pre-2000s"], band: null },
  }, // 1057
  {
    id: "deep-cuts",
    glyph: "🌑",
    hue: 248,
    name: "Deep Cuts",
    blurb: "The obscure tail — hard mode",
    filter: { genres: [], decades: [], band: "deep" },
  }, // 777
  {
    id: "right-now",
    glyph: "⚡",
    hue: 172,
    name: "Right Now",
    blurb: "Everything from the 2020s",
    filter: { genres: [], decades: ["2020s"], band: null },
  }, // 744
  {
    id: "stadium-rock",
    glyph: "🎸",
    hue: 6,
    name: "Stadium Rock",
    blurb: "Rock and metal, every era",
    filter: { genres: ["rock", "metal"], decades: [], band: null },
  }, // 744
  {
    id: "chart-toppers",
    glyph: "🏆",
    hue: 45,
    name: "Chart Toppers",
    blurb: "Only the songs everybody knows",
    filter: { genres: [], decades: [], band: "chart" },
  }, // 730
  {
    id: "the-2010s",
    glyph: "📱",
    hue: 205,
    name: "The 2010s",
    blurb: "The streaming decade",
    filter: { genres: [], decades: ["2010s"], band: null },
  }, // 633
  {
    id: "y2k",
    glyph: "💿",
    hue: 250,
    name: "Y2K",
    blurb: "All of the 2000s",
    filter: { genres: [], decades: ["2000s"], band: null },
  }, // 566
  {
    id: "hip-hop-era",
    glyph: "🎤",
    hue: 32,
    name: "Hip Hop Era",
    blurb: "Hip hop and R&B, start to now",
    filter: { genres: ["hip-hop", "rnb"], decades: [], band: null },
  }, // 540
  {
    id: "90s-rock",
    glyph: "🤘",
    hue: 350,
    name: "90s Rock",
    blurb: "Guitars, before the millennium",
    filter: { genres: ["rock", "metal", "alternative"], decades: ["pre-2000s"], band: null },
  }, // 368
  {
    id: "country-roads",
    glyph: "🤠",
    hue: 92,
    name: "Country Roads",
    blurb: "Country, every era",
    filter: { genres: ["country"], decades: [], band: null },
  }, // 324
  {
    id: "latin-heat",
    glyph: "💃",
    hue: 24,
    name: "Latin Heat",
    blurb: "Latin, every era",
    filter: { genres: ["latin"], decades: [], band: null },
  }, // 283
  {
    id: "2010s-pop",
    glyph: "✨",
    hue: 322,
    name: "2010s Pop",
    blurb: "Pop at its most inescapable",
    filter: { genres: ["pop"], decades: ["2010s"], band: null },
  }, // 179
];

export function mixById(id: string): Mix | null {
  return MIXES.find((m) => m.id === id) ?? null;
}

/**
 * How much of a Pool a player has already heard.
 *
 * The seen ids are intersected with the Pool rather than counted directly, so a
 * Catalog rebuild that drops Tracks can never show "heard 190 of 179".
 */
export function mixProgress(
  tracks: Track[],
  filter: Filter,
  seenIds: readonly number[] | undefined,
): { pool: number; heard: number } {
  const pool = tracksMatching(tracks, filter);
  if (!seenIds || seenIds.length === 0) return { pool: pool.length, heard: 0 };

  const seen = new Set(seenIds);
  return { pool: pool.length, heard: pool.reduce((n, t) => n + (seen.has(t.id) ? 1 : 0), 0) };
}
